// Generate Progress — plan §4.5.2 (#screen-gen-progress) + §4.5.3 (#screen-gen-done).
// SSE primary + 3sn polling fallback (karar #6).
// Per-section status, retry butonu ("Yeniden Dene") başarısızlar için,
// toplam % + anyFailed renk kodu, done ekranı §4.5.3 büyük ikon + CTA.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  formatReportingPeriod,
  type DocumentExportResponse,
  type GeneratedSectionRow,
  type Project,
  type ProjectGenerateStatusResponse,
} from "@mahir/shared";
import { api, ApiError } from "../api/client";
import { celebrateOnce } from "../lib/celebrate";

const POLL_INTERVAL_MS = 3000;

const STATUS_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  queued: { bg: "var(--color-bg)", fg: "var(--color-text-3)", label: "Beklemede" },
  running: {
    bg: "color-mix(in srgb, var(--color-primary) 14%, white)",
    fg: "var(--color-primary)",
    label: "Üretiliyor…",
  },
  success: {
    bg: "color-mix(in srgb, var(--color-success) 10%, white)",
    fg: "var(--color-success)",
    label: "Tamam",
  },
  failed: {
    bg: "color-mix(in srgb, var(--color-error) 10%, white)",
    fg: "var(--color-error)",
    label: "Başarısız",
  },
};

const FAILURE_REASON_TEXT: Record<string, string> = {
  auth: "LLM kimlik doğrulama hatası — yönetici ayarları kontrol etmeli",
  rate_limit: "LLM rate limit — tekrar deneyin",
  malformed: "Model beklenmeyen çıktı verdi",
  content_filter: "İçerik filtresi tetiklendi",
  network: "Bağlantı veya model hatası",
  timeout_stale: "Üretim zaman aşımına uğradı",
  timeout: "Model yanıtı zaman aşımına uğradı — tekrar deneyin",
  unknown: "Beklenmeyen model hatası",
  attachment:
    "Kanıt belgesi işlenemedi — belge formatını kontrol edin veya belgeyi kaldırıp yeniden üretin",
};

type GenerationEvent =
  | { type: "section"; row: GeneratedSectionRow }
  | { type: "run_completed"; projectId: string; version: number };

export function GenerateProgress(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [status, setStatus] = useState<ProjectGenerateStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sseConnected, setSseConnected] = useState(false);
  const [retryingCode, setRetryingCode] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Proje meta (müşteri adı + framework + yıl) — header için tek seferlik fetch.
  useEffect(() => {
    if (!id) return;
    let aborted = false;
    api<{ project: Project }>(`/projects/${id}`)
      .then((res) => {
        if (!aborted) setProject(res.project);
      })
      .catch(() => {
        // Header opsiyonel; status fetch zaten hata gösterecek.
      });
    return () => {
      aborted = true;
    };
  }, [id]);

  // Polling: 3sn (karar #6). SSE primary; polling her koşulda drift koruması için
  // çalışır — SSE event miss durumunda (hızlı dummy provider veya reconnect race)
  // sections[] + summary fresh snapshot'la senkronlar. Ek yük minimal.
  useEffect(() => {
    if (!id) return;
    let aborted = false;

    const load = async (): Promise<void> => {
      try {
        const s = await api<ProjectGenerateStatusResponse>(`/projects/${id}/generate/status`);
        if (!aborted) {
          setStatus(s);
          setError(null);
        }
      } catch (err: unknown) {
        if (!aborted) setError(err instanceof Error ? err.message : "Durum alınamadı");
      }
    };

    void load();
    const interval = setInterval(() => {
      if (aborted) return;
      void load();
    }, POLL_INTERVAL_MS);

    return () => {
      aborted = true;
      clearInterval(interval);
    };
  }, [id]);

  // SSE primary
  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`/api/v1/projects/${id}/generate/events`, {
      withCredentials: true,
    });
    es.addEventListener("open", () => setSseConnected(true));
    es.addEventListener("error", () => setSseConnected(false));
    const onGeneration = (ev: MessageEvent<string>): void => {
      try {
        const event = JSON.parse(ev.data) as GenerationEvent;
        if (event.type === "section") {
          setStatus((prev) => {
            if (!prev) return prev;
            const sections = prev.sections.map((s) => (s.id === event.row.id ? event.row : s));
            // Yeni row (row id listede yoksa) append
            if (!prev.sections.some((s) => s.id === event.row.id)) {
              sections.push(event.row);
            }
            const summary = recomputeSummary(sections);
            return { ...prev, sections, summary };
          });
        } else if (event.type === "run_completed") {
          setStatus((prev) => (prev ? { ...prev, active: false } : prev));
        }
      } catch {
        // ignore malformed
      }
    };
    es.addEventListener("generation", onGeneration as EventListener);
    return () => {
      es.removeEventListener("generation", onGeneration as EventListener);
      es.close();
      setSseConnected(false);
    };
  }, [id]);

  const handleRetry = async (sectionCode: string): Promise<void> => {
    setRetryingCode(sectionCode);
    setRetryError(null);
    try {
      await api(`/projects/${id}/generate/section/${sectionCode}`, {
        method: "POST",
        body: {},
      });
    } catch (err: unknown) {
      setRetryError(err instanceof Error ? err.message : "Yeniden deneme başarısız");
    } finally {
      setRetryingCode(null);
    }
  };

  if (error && !status) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p style={{ color: "var(--color-error)" }}>{error}</p>
      </div>
    );
  }
  if (!status) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <p style={{ color: "var(--color-text-2)" }}>Yükleniyor…</p>
      </div>
    );
  }

  const pct =
    status.summary.total === 0
      ? 0
      : Math.round((status.summary.completed / status.summary.total) * 100);
  const allDone = status.summary.total > 0 && !status.active;
  const anyFailed = status.summary.failed > 0;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div
        className="mb-2 text-xs uppercase tracking-wider flex items-center gap-2"
        style={{ color: "var(--color-text-3)" }}
      >
        <span>AI ÜRETİM · v{status.version.toString()}</span>
        <span
          className="rounded-full w-2 h-2 inline-block"
          title={sseConnected ? "SSE aktif" : "Polling fallback"}
          style={{
            background: sseConnected ? "var(--color-success)" : "var(--color-text-3)",
          }}
        />
      </div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          color: "var(--color-text)",
          fontSize: "1.75rem",
        }}
      >
        {project ? project.clientName : "Rapor üretimi"}
      </h1>
      {project && (
        <p className="mt-1 mb-6 text-sm" style={{ color: "var(--color-text-2)" }}>
          {project.frameworkName} ·{" "}
          {formatReportingPeriod(project.reportingYear, project.periodType)}
        </p>
      )}
      {!project && <div className="mb-6" />}

      <div
        className="rounded-xl p-5 mb-6"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {status.summary.completed.toString()} / {status.summary.total.toString()} bölüm
            {status.summary.running > 0 && ` · ${status.summary.running.toString()} aktif`}
            {status.summary.failed > 0 && ` · ${status.summary.failed.toString()} başarısız`}
          </span>
          <span className="text-sm" style={{ fontWeight: 600 }}>
            %{pct.toString()}
          </span>
        </div>
        <div
          className="h-2 rounded-full overflow-hidden"
          style={{ background: "var(--color-border)" }}
        >
          <div
            className="h-full transition-all"
            style={{
              width: `${pct.toString()}%`,
              background: anyFailed ? "var(--color-error)" : "var(--color-primary)",
            }}
          />
        </div>
      </div>

      {retryError && (
        <div
          className="rounded-md p-3 mb-4 text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {retryError}
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {[...status.sections]
          .sort((a, b) => a.sectionCode.localeCompare(b.sectionCode))
          .map((s) => {
            const col = STATUS_COLORS[s.generationStatus] ?? STATUS_COLORS.queued!;
            const showRetry = s.generationStatus === "failed";
            const isRetrying = retryingCode === s.sectionCode;
            return (
              <li
                key={s.id}
                className="rounded-lg p-3 flex items-center justify-between gap-3"
                style={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <div className="min-w-0 flex-1">
                  <div style={{ fontWeight: 600 }}>{s.sectionCode}</div>
                  {s.failureReason && (
                    <div className="text-xs mt-1" style={{ color: "var(--color-error)" }}>
                      {FAILURE_REASON_TEXT[s.failureReason] ?? s.failureReason}
                      {s.failureMessage && (
                        <span style={{ opacity: 0.6 }}> — {s.failureMessage}</span>
                      )}
                    </div>
                  )}
                  {s.generationStatus === "success" && s.tokensTotal !== null && (
                    <div
                      className="text-xs mt-1"
                      style={{ color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}
                    >
                      {s.tokensTotal.toString()} token
                      {s.costUsd && ` · $${Number(s.costUsd).toFixed(4)}`}
                    </div>
                  )}
                  {/* M11 — kullanılan kanıt belgeler (eng_review P3: ilk 5 + "+N daha") */}
                  {s.attachmentsUsed && s.attachmentsUsed.length > 0 && (
                    <div
                      className="text-xs mt-1.5 flex flex-wrap gap-x-2 gap-y-1 items-center"
                      style={{ color: "var(--color-text-3)" }}
                    >
                      <span>📎</span>
                      {s.attachmentsUsed.slice(0, 5).map((a) => (
                        <span
                          key={a.attachmentId}
                          title={`${a.questionCode} · ${Math.round(a.sizeBytes / 1024).toString()} KB`}
                          style={{ fontFamily: "var(--font-mono)" }}
                        >
                          {a.filename}
                        </span>
                      ))}
                      {s.attachmentsUsed.length > 5 && (
                        <span style={{ opacity: 0.7 }}>
                          +{(s.attachmentsUsed.length - 5).toString()} daha
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span
                  className="px-2 py-1 rounded-md text-xs whitespace-nowrap"
                  style={{ background: col.bg, color: col.fg, fontWeight: 600 }}
                >
                  {col.label}
                </span>
                {showRetry && (
                  <button
                    onClick={() => void handleRetry(s.sectionCode)}
                    disabled={isRetrying}
                    className="px-3 py-1 rounded-md text-xs disabled:opacity-50"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--color-primary)",
                      color: "var(--color-primary)",
                      fontWeight: 600,
                    }}
                  >
                    {isRetrying ? "…" : "Yeniden Dene"}
                  </button>
                )}
              </li>
            );
          })}
      </ul>

      {allDone && (
        <div className="mt-8 text-center">
          <div className="mb-2" style={{ fontSize: "3rem" }}>
            {anyFailed ? "⚠" : "✓"}
          </div>
          <h2 className="mb-3" style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem" }}>
            {anyFailed ? "Bazı bölümler başarısız oldu" : "Rapor hazır"}
          </h2>
          <p className="mb-2 text-sm" style={{ color: "var(--color-text-2)" }}>
            {status.summary.completed.toString()} bölüm üretildi.
            {status.summary.failed > 0 ? ` ${status.summary.failed.toString()} başarısız.` : ""}
          </p>
          <p className="mb-5 text-xs" style={{ color: "var(--color-text-3)" }}>
            Toplam{" "}
            {status.sections
              .reduce((acc, s) => acc + (s.tokensTotal ?? 0), 0)
              .toLocaleString("tr-TR")}{" "}
            token · $
            {status.sections.reduce((acc, s) => acc + Number(s.costUsd ?? 0), 0).toFixed(4)}
          </p>
          <div className="flex justify-center gap-3">
            <Link
              to={`/projects/${id}`}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-2)",
              }}
            >
              Proje Detayına Dön
            </Link>
            <button
              onClick={() => void navigate(`/projects/${id}/review`)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{
                background: "var(--color-primary)",
                color: "var(--color-surface)",
                fontWeight: 600,
              }}
            >
              Bölümleri Gözden Geçir
            </button>
            <button
              onClick={() => {
                setExportLoading(true);
                setExportError(null);
                api<DocumentExportResponse>(`/projects/${id}/documents/export/full`)
                  .then((res) => {
                    // M9-S4: ilk full docx indirildiğinde celebrate
                    celebrateOnce("first-full-export");
                    window.open(res.downloadUrl, "_blank");
                  })
                  .catch((err: unknown) => {
                    if (err instanceof ApiError) {
                      setExportError(
                        err.code === "not_all_approved"
                          ? "Önce tüm bölümleri onaylamanız gerekiyor."
                          : err.message,
                      );
                    } else setExportError("İndirilemedi");
                  })
                  .finally(() => setExportLoading(false));
              }}
              disabled={
                exportLoading || anyFailed || status.summary.completed !== status.summary.total
              }
              title={
                status.summary.completed !== status.summary.total
                  ? "Tüm bölümler tamamlanmadan indirilemez"
                  : "Full rapor — sadece approved bölümler dahil"
              }
              className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-2)",
              }}
            >
              {exportLoading ? "İndiriliyor…" : "Raporu İndir"}
            </button>
            {exportError && (
              <div
                className="mt-2 px-3 py-2 rounded text-xs w-full"
                style={{
                  background: "color-mix(in srgb, var(--color-error) 10%, white)",
                  color: "var(--color-error)",
                  border: "1px solid rgba(198,40,40,0.2)",
                }}
              >
                {exportError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function recomputeSummary(
  sections: GeneratedSectionRow[],
): ProjectGenerateStatusResponse["summary"] {
  const out = { total: 0, completed: 0, running: 0, queued: 0, failed: 0 };
  for (const s of sections) {
    out.total += 1;
    if (s.generationStatus === "success") out.completed += 1;
    else if (s.generationStatus === "running") out.running += 1;
    else if (s.generationStatus === "queued") out.queued += 1;
    else if (s.generationStatus === "failed") out.failed += 1;
  }
  return out;
}
